// Where the island's small repeated objects go: trees, rocks, ore, flowers, clouds and sea sparkles.
// Placement follows the mockup (scene.js) call for call, so the island looks the same,
// and the stress mode copies every object a fixed number of times.
import { cellKey, ownedCells, parseKey, TERRAIN, SEA_Y, worldX, worldZ, type Island } from './island';
import { seeded } from './random';

export type DecorKind = 'trunk' | 'crown' | 'boulder' | 'pebble' | 'ore' | 'tuft' | 'cloud' | 'sparkle';

/** One instance of a prototype mesh; sizes are multipliers of the prototype. */
export interface DecorPart {
  readonly kind: DecorKind;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rx: number;
  readonly ry: number;
  readonly rz: number;
  readonly sx: number;
  readonly sy: number;
  readonly sz: number;
  readonly color: string;
}

/** A group of parts that moves as one when the stress mode scatters copies. */
export interface DecorObject {
  /** Cell the object belongs to; copies stay inside it. Absent for sea sparkles. */
  readonly cell?: readonly [number, number];
  readonly parts: readonly DecorPart[];
}

const AUTUMN = ['#f7931e', '#ffa62b', '#f5a01f', '#ffb43a'] as const;
const GREEN = ['#6fae3e', '#7fbf45'] as const;
const TRUNK = '#e9e2d6';
const PEBBLE = '#8f8c86';
const BOULDER = '#a39d93';
const ORE = '#a0563f';
const ORE_COPPER = '#e58a3b';
const TUFT = '#e2404f';
const CLOUD = '#ffffff';
const SPARKLE = '#9cc8ff';

/** Prototype sizes the parts scale: crowns are 0.15 wide, the second crown 0.1. */
export const CROWN_RADIUS = 0.15;

function part(kind: DecorKind, x: number, y: number, z: number, color: string, extra: Partial<DecorPart> = {}): DecorPart {
  return { kind, x, y, z, rx: 0, ry: 0, rz: 0, sx: 1, sy: 1, sz: 1, color, ...extra };
}

const pick = <T>(list: readonly T[], r: number): T => list[Math.min(list.length - 1, Math.floor(r * list.length))] as T;

function tree(x: number, y: number, z: number, rnd: () => number, kind: 'autumn' | 'green', size: number): DecorObject['parts'] {
  const s = size * 1.25;
  const color = kind === 'green' ? pick(GREEN, rnd()) : pick(AUTUMN, rnd());
  const h = 1.6 + rnd() * 0.6;
  const parts: DecorPart[] = [
    part('trunk', x, y + 0.13 * s, z, TRUNK, { sx: s, sy: s, sz: s }),
    part('crown', x, y + (0.22 + 0.15 * h * 0.8) * s, z, color, { ry: rnd() * 3, sx: s, sy: h * s, sz: s }),
  ];
  if (rnd() < 0.45) {
    const k = (0.1 / CROWN_RADIUS) * s;
    parts.push(part('crown', x + 0.05 * s, y + (0.22 + 0.15 * h * 1.5) * s, z + 0.02 * s, color, { ry: rnd() * 3, sx: k, sy: 1.4 * k, sz: k }));
  }
  return parts;
}

function pebble(x: number, y: number, z: number, size: number): DecorPart {
  return part('pebble', x, y + size * 0.5, z, PEBBLE, { ry: x * 3, sx: size, sy: size, sz: size });
}

function tuft(x: number, y: number, z: number): DecorPart[] {
  return [0, 1, 2, 3].map((i) => part('tuft', x + (i - 1.5) * 0.015, y + 0.04, z, TUFT, { rz: (i - 1.5) * 0.3 }));
}

export function puff(x: number, y: number, z: number, size: number): DecorPart {
  return part('cloud', x, y, z, CLOUD, { sx: size, sy: size, sz: size });
}

/** Steam and smoke puffs of buildings, placed by the building builder. */
export interface ExtraPuff {
  readonly cell: readonly [number, number];
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly size: number;
}

export function placeDecor(island: Island, extraPuffs: readonly ExtraPuff[] = []): DecorObject[] {
  const objects: DecorObject[] = [];
  const owned = ownedCells(island);
  for (const [key, code] of island.terrain) {
    const [c, r] = parseKey(key);
    const cell = [c, r] as const;
    const x = worldX(island, c);
    const z = worldZ(island, r);
    const t = TERRAIN[code].top;
    const rnd = seeded(c, r);
    const busy = owned.has(key);
    const add = (parts: readonly DecorPart[]): void => {
      objects.push({ cell, parts });
    };
    if (code === 'b') {
      const n = busy ? 2 : 5;
      for (let i = 0; i < n; i++) {
        const px = x + (rnd() - 0.5) * 0.75;
        const pz = z + (rnd() - 0.5) * 0.75;
        const kind = rnd() < 0.2 ? 'green' : 'autumn';
        add(tree(px, t, pz, rnd, kind, 0.85 + rnd() * 0.3));
      }
    } else if (code === 'p') {
      if (!busy && rnd() < 0.35) {
        const px = x + (rnd() - 0.5) * 0.6;
        const pz = z + (rnd() - 0.5) * 0.6;
        add(tree(px, t, pz, rnd, 'autumn', 0.8));
      }
      if (rnd() < 0.5) {
        const px = x + (rnd() - 0.5) * 0.7;
        const pz = z + (rnd() - 0.5) * 0.7;
        add(tuft(px, t, pz));
      }
      if (rnd() < 0.4) {
        const px = x + (rnd() - 0.5) * 0.7;
        const pz = z + (rnd() - 0.5) * 0.7;
        add([pebble(px, t, pz, 0.04)]);
      }
    } else if (code === 'h' || code === 'c' || code === 'm') {
      const n = busy ? 1 : 3;
      for (let i = 0; i < n; i++) {
        const s = 0.16 + rnd() * 0.1;
        const px = x + (busy ? -0.28 : (rnd() - 0.5) * 0.55);
        const pz = z + (busy ? -0.28 : (rnd() - 0.5) * 0.55);
        const parts: DecorPart[] = [part('boulder', px, t + s * 0.7, pz, BOULDER, { ry: rnd() * 3, sx: s, sy: s * 1.4, sz: s })];
        if (code !== 'm') {
          for (let j = 0; j < 3; j++) {
            const ox = px + (rnd() - 0.5) * s * 1.4;
            const oy = t + 0.05 + rnd() * s;
            parts.push(part('ore', ox, oy, pz + s * 0.7, code === 'h' ? ORE : ORE_COPPER));
          }
        }
        add(parts);
      }
    } else if (code === 'a') {
      if (!busy && rnd() < 0.3) {
        const px = x + (rnd() - 0.5) * 0.6;
        const pz = z + (rnd() - 0.5) * 0.6;
        add([pebble(px, t, pz, 0.05)]);
      }
    } else if (code === 't') {
      if (!busy) add([puff(x + 0.1, t + 0.25, z, 0.08), puff(x + 0.16, t + 0.4, z - 0.05, 0.06)]);
    }
  }

  // Clouds over every other undiscovered cell, leaving the rocket's cape visible.
  const [goalC, goalR] = island.goal;
  for (const key of island.fog) {
    const [c, r] = parseKey(key);
    if ((c + r) % 2 || (c === goalC && r === goalR)) continue;
    const code = island.terrain.get(cellKey(c, r));
    if (!code) continue;
    const rnd = seeded(c, r, 7);
    const x = worldX(island, c);
    const z = worldZ(island, r);
    const t = TERRAIN[code].top;
    const parts: DecorPart[] = [];
    for (let i = 0; i < 3; i++) {
      const px = x + (rnd() - 0.5) * 0.6;
      const py = t + 0.32 + rnd() * 0.1;
      const pz = z + (rnd() - 0.5) * 0.6;
      parts.push(puff(px, py, pz, 0.15 + rnd() * 0.1));
    }
    objects.push({ cell: [c, r], parts });
  }

  for (const p of extraPuffs) objects.push({ cell: p.cell, parts: [puff(p.x, p.y, p.z, p.size)] });

  // Light sparkles on the sea.
  const rnd = seeded(3, 3);
  for (let i = 0; i < 160; i++) {
    const width = 0.06 + rnd() * 0.1;
    const x = (rnd() - 0.5) * 30;
    const z = (rnd() - 0.5) * 30;
    objects.push({ parts: [part('sparkle', x, SEA_Y + 0.002, z, SPARKLE, { rx: -Math.PI / 2, rz: 0.6, sx: width })] });
  }
  return objects;
}

/**
 * Returns the objects plus `copies - 1` scattered copies of each one: the stress mode's
 * "five times more objects". Copies keep their cell (and so their height) and are deterministic.
 */
export function multiplyDecor(island: Island, objects: readonly DecorObject[], copies: number): DecorObject[] {
  if (!Number.isInteger(copies) || copies < 1) throw new Error('copies debe ser un entero positivo');
  const out: DecorObject[] = [...objects];
  objects.forEach((object, index) => {
    const anchor = object.parts[0];
    if (!anchor) return;
    for (let k = 1; k < copies; k++) {
      let dx: number;
      let dz: number;
      if (object.cell) {
        const [c, r] = object.cell;
        const rnd = seeded(c, r, 1000 + index * 8 + k);
        dx = worldX(island, c) + (rnd() - 0.5) * 0.7 - anchor.x;
        dz = worldZ(island, r) + (rnd() - 0.5) * 0.7 - anchor.z;
      } else {
        const rnd = seeded(index, k, 2000);
        dx = (rnd() - 0.5) * 30 - anchor.x;
        dz = (rnd() - 0.5) * 30 - anchor.z;
      }
      out.push({
        ...(object.cell ? { cell: object.cell } : {}),
        parts: object.parts.map((p) => ({ ...p, x: p.x + dx, z: p.z + dz })),
      });
    }
  });
  return out;
}

export function countParts(objects: readonly DecorObject[]): Record<DecorKind, number> {
  const counts: Record<DecorKind, number> = { trunk: 0, crown: 0, boulder: 0, pebble: 0, ore: 0, tuft: 0, cloud: 0, sparkle: 0 };
  for (const o of objects) for (const p of o.parts) counts[p.kind]++;
  return counts;
}
